import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotify, Title, useDataProvider, usePermissions } from 'react-admin';
import {
	Alert, Box, Button, Checkbox, CircularProgress,
	FormControlLabel, IconButton, LinearProgress, Tab, Tabs, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import { deleteFile, getFile, listDirectory, putTextFile, uploadBinary } from './githubApi';
import { useGithubConfig, GitHubImage } from './ra_contentEditor';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function UploadButton({ label, accept, onFiles, multiple, disabled }) {
	const ref = useRef();
	return (
		<>
			<input
				type="file" accept={accept} ref={ref} style={{ display: 'none' }}
				multiple={!!multiple}
				onChange={e => {
					const files = Array.from(e.target.files);
					if (files.length) onFiles(files);
					e.target.value = '';
				}}
			/>
			<Button variant="outlined" size="small" startIcon={<UploadFileIcon />}
				onClick={() => ref.current.click()} disabled={disabled}>
				{label}
			</Button>
		</>
	);
}

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

function BannerEditor({ pat, owner, repo, branch }) {
	const notify = useNotify();
	const [saving, setSaving] = useState(false);
	const [buster, setBuster] = useState(0);

	const handleUpload = async ([file]) => {
		setSaving(true);
		try {
			const buf = await file.arrayBuffer();
			let existingSha = null;
			try { existingSha = (await getFile(pat, owner, repo, 'banner/banner.png', branch)).sha; } catch {}
			await uploadBinary(pat, owner, repo, 'banner/banner.png', branch, buf, existingSha, 'Update banner');
			notify('Banner uploaded.', { type: 'success' });
			setBuster(b => b + 1);
		} catch (e) {
			notify(`Upload failed: ${e.message}`, { type: 'error' });
		}
		setSaving(false);
	};

	return (
		<Box>
			<Typography variant="subtitle2" gutterBottom>Current banner</Typography>
			<Box mb={2} sx={{ maxWidth: 600, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
				<GitHubImage key={buster} pat={pat} owner={owner} repo={repo} branch={branch} fallbackBranch="main"
					filePath="banner/banner.png"
					imgStyle={{ width: '100%', display: 'block', objectFit: 'cover' }}
					placeholder={<img src={`${process.env.PUBLIC_URL}/content/banner/banner.png`} alt="banner" style={{ width: '100%', display: 'block', objectFit: 'cover' }} />} />
			</Box>
			<UploadButton label={saving ? 'Uploading…' : 'Replace banner'} accept="image/*"
				onFiles={handleUpload} disabled={saving} />
			<Typography variant="caption" color="text.secondary" display="block" mt={1}>
				Replaces <code>banner/banner.png</code>. Takes effect after deploy.
			</Typography>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

function GalleryEditor({ pat, owner, repo, branch }) {
	const notify = useNotify();
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [progress, setProgress] = useState(null); // e.g. "2 / 5"

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const items = await listDirectory(pat, owner, repo, 'gallery', branch);
			setFiles(items.filter(f => f.type === 'file'));
		} catch (e) {
			notify(`Could not load gallery: ${e.message}`, { type: 'error' });
		}
		setLoading(false);
	}, [pat, owner, repo, branch, notify]);

	useEffect(() => { load(); }, [load]);

	const handleUpload = async (selectedFiles) => {
		setProgress(`0 / ${selectedFiles.length}`);
		let done = 0;
		for (const file of selectedFiles) {
			try {
				const buf = await file.arrayBuffer();
				await uploadBinary(pat, owner, repo, `gallery/${file.name}`, branch, buf, null, `Add gallery image ${file.name}`);
				done++;
				setProgress(`${done} / ${selectedFiles.length}`);
			} catch (e) {
				notify(`Failed to upload ${file.name}: ${e.message}`, { type: 'error' });
			}
		}
		notify(`${done} / ${selectedFiles.length} images uploaded.`, { type: 'success' });
		setProgress(null);
		await load();
	};

	const handleDelete = async (f) => {
		if (!window.confirm(`Delete ${f.name}?`)) return;
		try {
			await deleteFile(pat, owner, repo, `gallery/${f.name}`, branch, f.sha, `Delete gallery image ${f.name}`);
			notify(`${f.name} deleted.`, { type: 'success' });
			setFiles(prev => prev.filter(x => x.name !== f.name));
		} catch (e) {
			notify(`Delete failed: ${e.message}`, { type: 'error' });
		}
	};

	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
				<Typography variant="subtitle2">{files.length} images</Typography>
				<IconButton size="small" onClick={load} disabled={loading || !!progress}>
					<RefreshIcon fontSize="small" />
				</IconButton>
				<UploadButton
					label={progress ? `Uploading ${progress}…` : 'Upload images'}
					accept="image/*" multiple
					onFiles={handleUpload}
					disabled={!!progress || loading}
				/>
			</Box>
			{progress && <LinearProgress sx={{ mb: 2 }} />}
			{loading ? <CircularProgress size={24} /> : (
				<Box display="flex" flexWrap="wrap" gap={1.5}>
					{files.map(f => (
						<Box key={f.name} sx={{
							position: 'relative', width: 140,
							border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
							'&:hover .del': { opacity: 1 },
						}}>
							<GitHubImage pat={pat} owner={owner} repo={repo} branch={branch}
								filePath={`gallery/${f.name}`}
								imgStyle={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
							<Typography variant="caption" sx={{ display: 'block', p: 0.5, wordBreak: 'break-all', fontSize: 9, lineHeight: 1.2, color: 'text.primary' }}>
								{f.name}
							</Typography>
							<IconButton className="del" size="small" onClick={() => handleDelete(f)}
								sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', opacity: 0, transition: 'opacity 0.15s', p: 0.5 }}>
								<DeleteIcon fontSize="small" color="error" />
							</IconButton>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
}

// ---------------------------------------------------------------------------
// One-time import: copy the legacy GitHub-hosted people.json /
// extProjectsConfig.json into the new Firestore `people` / `collaborations`
// collections (see ra_people.js / ra_collaborations.js). Delete this tab once
// the import has been run and verified — it's a migration tool, not a
// permanent editor. The people.json `collaborations` array (a third, unused
// section distinct from extProjectsConfig.json's "Collaborations") is
// intentionally skipped: no public page ever reads it.
// ---------------------------------------------------------------------------

function ImportLegacyContentTool({ pat, owner, repo, branch }) {
	const notify = useNotify();
	const dataProvider = useDataProvider();
	const { permissions } = usePermissions();
	const [checking, setChecking] = useState(true);
	const [peopleCount, setPeopleCount] = useState(null);
	const [collabCount, setCollabCount] = useState(null);
	const [importing, setImporting] = useState(false);
	const [force, setForce] = useState(false);
	const [summary, setSummary] = useState(null);

	const checkCounts = useCallback(async () => {
		setChecking(true);
		try {
			const [peopleSnap, collabSnap] = await Promise.all([
				getDocs(collection(db, 'people')),
				getDocs(collection(db, 'collaborations')),
			]);
			setPeopleCount(peopleSnap.size);
			setCollabCount(collabSnap.size);
		} catch (e) {
			notify(`Could not check Firestore: ${e.message}`, { type: 'error' });
		}
		setChecking(false);
	}, [notify]);

	useEffect(() => { checkCounts(); }, [checkCounts]);

	const runImport = async () => {
		setImporting(true);
		setSummary(null);
		try {
			let peopleImported = 0;
			let collabImported = 0;

			const peopleFile = await getFile(pat, owner, repo, 'people/people.json', branch).catch(() => null);
			if (peopleFile) {
				const parsed = JSON.parse(peopleFile.content);
				for (const p of (parsed.team ?? [])) {
					const { id, ...rest } = p;
					await dataProvider.create('people', { data: { ...rest, type: 'team' } });
					peopleImported++;
				}
				for (const p of (parsed.clients ?? [])) {
					const { id, ...rest } = p;
					await dataProvider.create('people', { data: { ...rest, type: 'client' } });
					peopleImported++;
				}
			}

			const collabFile = await getFile(pat, owner, repo, 'projects/extProjectsConfig.json', branch).catch(() => null);
			if (collabFile) {
				const parsed = JSON.parse(collabFile.content);
				for (const c of parsed) {
					const { id, nameid, buttonText, ...rest } = c;
					await dataProvider.create('collaborations', { data: rest });
					collabImported++;
				}
			}

			setSummary(`Imported ${peopleImported} people and ${collabImported} collaborations.`);
			notify('Import complete.', { type: 'success' });
			setForce(false);
			checkCounts();
		} catch (e) {
			notify(`Import failed: ${e.message}`, { type: 'error' });
		}
		setImporting(false);
	};

	if (permissions < 99) {
		return <Alert severity="error">Admin access required (role 99).</Alert>;
	}

	const alreadyHasData = (peopleCount > 0) || (collabCount > 0);

	return (
		<Box>
			<Alert severity="info" sx={{ mb: 2 }}>
				One-time tool: copies <code>people/people.json</code> (team + clients) and{' '}
				<code>projects/extProjectsConfig.json</code> from GitHub into the new{' '}
				<strong>People</strong> and <strong>Collaborations</strong> Firestore collections.
				Requires Firestore's public-read security rules to already be updated (see the
				migration plan) — this only affects the admin write side. Delete this tab once
				you've confirmed the import worked.
			</Alert>

			{checking ? (
				<CircularProgress size={24} />
			) : (
				<Typography variant="body2" color="text.secondary" mb={2}>
					Firestore currently has <strong>{peopleCount}</strong> people and{' '}
					<strong>{collabCount}</strong> collaborations.
				</Typography>
			)}

			{alreadyHasData && !force && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Firestore already has records in one or both collections — importing again would
					create duplicates.
					<FormControlLabel
						sx={{ display: 'block', mt: 1 }}
						control={<Checkbox checked={force} onChange={e => setForce(e.target.checked)} size="small" />}
						label="Import anyway (I understand this may create duplicates)"
					/>
				</Alert>
			)}

			{summary && <Alert severity="success" sx={{ mb: 2 }}>{summary}</Alert>}

			<Button
				variant="contained"
				onClick={runImport}
				disabled={importing || checking || (alreadyHasData && !force)}
				startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
			>
				{importing ? 'Importing…' : 'Run import'}
			</Button>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const COLOR_KEYS = [
	{ key: 'accent', label: 'Accent' },
	{ key: 'darkAccent', label: 'Dark accent' },
	{ key: 'darkSecondary', label: 'Dark secondary' },
	{ key: 'secondary', label: 'Secondary' },
	{ key: 'background', label: 'Background' },
	{ key: 'backgroundDark', label: 'Background dark' },
	{ key: 'text', label: 'Text' },
];

const DEFAULT_COLORS = {
	accent: '#ffffffff',
	darkAccent: '#478ec4',
	darkSecondary: '#80a327',
	secondary: '#a9d734',
	background: '#FFFFFF',
	backgroundDark: '#e7e7e7ff',
	text: '#333333',
};

function ColorsEditor({ pat, owner, repo, branch }) {
	const notify = useNotify();
	const [colors, setColors] = useState(DEFAULT_COLORS);
	const [sha, setSha] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		getFile(pat, owner, repo, 'style.json', branch)
			.then(({ content, sha: s }) => {
				const parsed = JSON.parse(content);
				setColors({ ...DEFAULT_COLORS, ...(parsed.colors ?? {}) });
				setSha(s);
			})
			.catch(() => {})
			.finally(() => setLoading(false));
	}, [pat, owner, repo, branch]);

	const setColor = (key, val) => {
		setColors(c => ({ ...c, [key]: val }));
		document.documentElement.style.setProperty(`--color-${key}`, val);
	};

	const save = async () => {
		setSaving(true);
		try {
			const res = await putTextFile(pat, owner, repo, 'style.json', branch,
				JSON.stringify({ colors }, null, 2), sha, 'Update site colors');
			setSha(res.content.sha);
			notify('Colors saved. Deploy to make them live.', { type: 'success' });
		} catch (e) {
			notify(`Save failed: ${e.message}`, { type: 'error' });
		}
		setSaving(false);
	};

	if (loading) return <CircularProgress size={24} />;

	return (
		<Box>
			<Alert severity="info" sx={{ mb: 2 }}>
				Color changes preview instantly in the browser. They go live on the public site after you deploy.
			</Alert>
			<Box display="flex" flexWrap="wrap" gap={3} mb={3}>
				{COLOR_KEYS.map(({ key, label }) => (
					<Box key={key} display="flex" alignItems="center" gap={1.5}>
						<input type="color" value={colors[key]?.slice(0, 7) ?? '#000000'}
							onChange={e => setColor(key, e.target.value)}
							style={{ width: 44, height: 44, border: '1px solid rgba(128,128,128,0.4)', cursor: 'pointer', borderRadius: 6, padding: 2 }} />
						<Box>
							<Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
							<Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>{colors[key]}</Typography>
						</Box>
					</Box>
				))}
			</Box>
			<Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
				onClick={save} disabled={saving}>
				{saving ? 'Saving…' : 'Save colors'}
			</Button>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

const SiteContentPage = () => {
	const { ghConfig, loading, error } = useGithubConfig();
	const [tab, setTab] = useState(0);

	return (
		<Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
			<Title title="Site Content" />

			{loading && <CircularProgress />}

			{!loading && (error || !ghConfig) && (
				<Alert severity="warning" action={<Button size="small" href="#/admin/settings/github">Go to settings</Button>}>
					GitHub settings not configured.
				</Alert>
			)}

			{!loading && ghConfig && (() => {
				const { pat, owner, repo, branch } = ghConfig;
				return (
					<Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, boxShadow: 1 }}>
						<Typography variant="h6" mb={2} color="text.primary">Site Content</Typography>
						<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
							<Tab label="Banner" />
							<Tab label="Gallery" />
							<Tab label="Import to Firestore" />
							<Tab label="Colors" />
						</Tabs>
						{tab === 0 && <BannerEditor pat={pat} owner={owner} repo={repo} branch={branch} />}
						{tab === 1 && <GalleryEditor pat={pat} owner={owner} repo={repo} branch={branch} />}
						{tab === 2 && <ImportLegacyContentTool pat={pat} owner={owner} repo={repo} branch={branch} />}
						{tab === 3 && <ColorsEditor pat={pat} owner={owner} repo={repo} branch={branch} />}
					</Box>
				);
			})()}
		</Box>
	);
};

export default SiteContentPage;
